/**
 * Question Domain Model
 * Represents a question within a section
 */

import { isFillInAnswerCorrect } from '@utils/fillInAnswerMatching';

export class Question {
  constructor(data) {
    this.id = data.id;
    this.sectionId = data.sectionId;
    this.index = data.index;
    this.type = data.type; // 'multiple-choice', 'true-false', 'fill-in', 'multi-select', 'matching', 'drag-drop', 'code'
    this.difficulty = data.difficulty; // 'easy', 'medium', 'hard'
    this.text = data.text;
    this.options = data.options || [];
    this.correctAnswer = data.correctAnswer;
    this.explanation = data.explanation;
    this.hints = data.hints || [];
    this.tags = data.tags || [];
    this.codeTemplate = data.codeTemplate || null; // For code questions
    this.testCases = data.testCases || []; // For code questions
    
    // For matching questions
    this.leftItems = data.leftItems || [];
    this.rightItems = data.rightItems || [];
    
    // For drag-drop questions
    this.items = data.items || [];
  }

  /**
   * Check if answer is correct
   */
  isCorrect(userAnswer) {
    if (this.type === 'multiple-choice') {
      return userAnswer === this.correctAnswer;
    }
    if (this.type === 'true-false') {
      const normalized = String(userAnswer).trim().toLowerCase();
      const correct = String(this.correctAnswer).trim().toLowerCase();
      return normalized === correct;
    }
    if (this.type === 'fill-in') {
      return isFillInAnswerCorrect(userAnswer, this.correctAnswer);
    }
    if (this.type === 'multi-select') {
      // userAnswer is array of selected option IDs
      // correctAnswer is array of correct option IDs
      if (!Array.isArray(userAnswer) || !Array.isArray(this.correctAnswer)) return false;
      if (userAnswer.length !== this.correctAnswer.length) return false;

      const options = Array.isArray(this.options) ? this.options : [];
      const getOptionKey = (option, index) => {
        if (option == null) return index;
        if (typeof option === 'string' || typeof option === 'number') return option;
        return option.id ?? option.value ?? option.key ?? option.text ?? index;
      };

      const normalizeAnswer = (answers) => {
        return answers.map(answer => {
          const matchIndex = options.findIndex((opt, idx) => (
            opt?.id === answer ||
            opt?.value === answer ||
            opt?.key === answer ||
            opt?.text === answer ||
            idx === answer
          ));

          if (matchIndex >= 0) {
            return getOptionKey(options[matchIndex], matchIndex);
          }

          return answer;
        });
      };

      const normalizedUser = normalizeAnswer(userAnswer);
      const normalizedCorrect = normalizeAnswer(this.correctAnswer);

      const sortedUser = [...normalizedUser].sort();
      const sortedCorrect = [...normalizedCorrect].sort();
      return sortedUser.every((val, idx) => val === sortedCorrect[idx]);
    }
    if (this.type === 'matching') {
      // userAnswer is object mapping left IDs to right IDs
      // correctAnswer is object with correct pairs
      if (typeof userAnswer !== 'object' || typeof this.correctAnswer !== 'object') return false;
      
      const userKeys = Object.keys(userAnswer);
      const correctKeys = Object.keys(this.correctAnswer);
      
      if (userKeys.length !== correctKeys.length) return false;
      
      return userKeys.every(leftId => 
        userAnswer[leftId] === this.correctAnswer[leftId]
      );
    }
    if (this.type === 'drag-drop') {
      // userAnswer is array of item IDs in user's order
      // correctAnswer is array of item IDs in correct order
      if (!Array.isArray(userAnswer) || !Array.isArray(this.correctAnswer)) return false;
      if (userAnswer.length !== this.correctAnswer.length) return false;
      
      return userAnswer.every((val, idx) => val === this.correctAnswer[idx]);
    }
    // For code questions, this will be handled by test cases
    return false;
  }

  /**
   * Get hint by index
   */
  getHint(index) {
    return this.hints[index] || null;
  }

  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      id: this.id,
      sectionId: this.sectionId,
      index: this.index,
      type: this.type,
      difficulty: this.difficulty,
      text: this.text,
      options: this.options,
      correctAnswer: this.correctAnswer,
      explanation: this.explanation,
      hints: this.hints,
      tags: this.tags,
      codeTemplate: this.codeTemplate,
      testCases: this.testCases,
      leftItems: this.leftItems,
      rightItems: this.rightItems,
      items: this.items,
    };
  }

  /**
   * Create from JSON
   */
  static fromJSON(json) {
    return new Question(json);
  }
}
